import { cx } from "@emotion/css";
import { ReactElement, memo, useMemo } from "react";
import { useGroup } from "~/hooks/useGroup";
import { useTagRecords } from "~/hooks/useTagRecords";
import { useThreadPermittedGroups } from "~/hooks/useThreadPermittedGroups";
import { Tooltip } from "./Tooltip";
import { isEqual } from "libs/predicates";
import { useThreadResolvedTag } from "~/hooks/useThreadResolvedTag";
import { markThreadNotResolvedCommand } from "~/utils/common-commands";
import { LabelTagRecord, ThreadResolvedThreadTagRecord } from "libs/schema";
import { MdLabelOutline, MdRemoveCircle } from "react-icons/md";
import { renderGroupName } from "~/utils/tag-utils";
import { useUserProfile } from "~/hooks/useUserProfile";
import { useGroups } from "~/hooks/useGroups";
import { ParentComponent } from "~/utils/type-helpers";
import { Link, useParams } from "@tanstack/react-router";
import { useLabel } from "~/hooks/useLabel";
import { useThreadLabelIds } from "~/hooks/useThreadLabelIds";

export const LabelContainer: ParentComponent<{ className?: string }> = (props) => {
  if (!props.children) return null;

  return <div className={props.className ?? "flex space-x-2"}>{props.children}</div>;
};

/* -----------------------------------------------------------------------------------------------*/

export const LabelChip: ParentComponent<{
  href?: string;
  tooltip?: string | ReactElement;
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  colorClassName?: string;
  className?: string;
  removeButtonTooltip?: string;
  onRemoveButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
}> = (props) => {
  if (props.onRemoveButtonClick) {
    return (
      <RemoveLabelButton
        tooltip={props.removeButtonTooltip || "Remove label"}
        onClick={props.onRemoveButtonClick}
        className={props.className}
      >
        <InnerLabelChip
          tooltip={props.tooltip}
          href={props.href}
          onClick={props.onClick}
          colorClassName={props.colorClassName}
        >
          {props.children}
        </InnerLabelChip>
      </RemoveLabelButton>
    );
  }

  return <InnerLabelChip {...props} />;
};

/* -----------------------------------------------------------------------------------------------*/

const InnerLabelChip: ParentComponent<{
  href?: string;
  tooltip?: string | ReactElement;
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  colorClassName?: string;
  className?: string;
}> = (props) => {
  if (props.href) {
    return (
      <Tooltip side="bottom" content={props.tooltip}>
        <Link
          to={props.href}
          className={cx(
            LabelSizeClasses,
            props.colorClassName || LabelOutlineColorClasses + " hover:border-black hover:text-black",
            props.className || "flex items-center",
          )}
          onClick={props.onClick}
        >
          {props.children}
        </Link>
      </Tooltip>
    );
  } else if (props.onClick) {
    return (
      <Tooltip side="bottom" content={props.tooltip}>
        <button
          type="button"
          className={cx(
            LabelSizeClasses,
            props.colorClassName || LabelOutlineColorClasses + " hover:border-black hover:text-black",
            props.className,
          )}
          onClick={props.onClick}
        >
          {props.children}
        </button>
      </Tooltip>
    );
  } else {
    return (
      <Tooltip side="bottom" content={props.tooltip}>
        <span
          className={cx(
            LabelSizeClasses,
            props.colorClassName || LabelOutlineColorClasses,
            props.tooltip && "hover:cursor-help",
            props.className,
          )}
        >
          {props.children}
        </span>
      </Tooltip>
    );
  }
};

const LabelSizeClasses = `
  border rounded text-xs truncate max-w-40 pt-[3px] 
  px-[6px] pb-[2px] font-medium
`;

const LabelOutlineColorClasses = `
  bg-transparent border-slateA-9 text-slateA-9 
`;

const LabelOutlineClasses = LabelSizeClasses + LabelOutlineColorClasses;

/* -----------------------------------------------------------------------------------------------*/

export const PermittedGroupChips: ParentComponent<{ threadId: string }> = memo((props) => {
  const [groups] = useThreadPermittedGroups({ threadId: props.threadId }, { fetchStrategy: "cache" });

  const params = useParams({ strict: false });

  const { nonOrgGroups, overflowGroups } = useMemo(() => {
    const nonOrgGroups =
      params.tagId ?
        // If we're on a page associated with a specific group then we
        // don't want to include that group when rendering the labels
        // (the inclusion of that group is implied).
        groups.filter((group) => group.id !== params.tagId && !group.data?.is_organization_group)
      : groups.filter((group) => !group.data?.is_organization_group);

    const overflowGroups = nonOrgGroups.slice(2).map((g) => g.id);
    return { nonOrgGroups, overflowGroups };
  }, [params.tagId, groups]);

  return (
    <>
      {nonOrgGroups.slice(0, 2).map((group) => {
        return <GroupLabel key={group.id} groupId={group.id} />;
      })}

      {overflowGroups.length > 0 && <GroupLabelOverflow groupIds={overflowGroups} />}
    </>
  );
}, isEqual);

/* -----------------------------------------------------------------------------------------------*/

export const GroupLabels: ParentComponent<{ groupIds: string[] }> = memo((props) => {
  const params = useParams({ strict: false });

  // If we're on a page associated with a specific group then we
  // don't need to include that group when rendering the labels
  // (i.e. the inclusion of that group is implied).
  const groupIds = useMemo(
    () => (params.tagId ? props.groupIds.filter((id) => id !== params.tagId) : props.groupIds),
    [params.tagId, props.groupIds],
  );

  const [groups] = useGroups(groupIds);

  const { nonOrgGroups, overflowGroups } = useMemo(() => {
    const nonOrgGroups = groups.filter((group) => !group.data?.is_organization_group);
    const overflowGroups = nonOrgGroups.slice(2).map((g) => g.id);
    return { nonOrgGroups, overflowGroups };
  }, [groups]);

  return (
    <>
      {nonOrgGroups.slice(0, 2).map((group) => {
        return <GroupLabel key={group.id} groupId={group.id} />;
      })}

      {overflowGroups.length > 0 && <GroupLabelOverflow groupIds={overflowGroups} />}
    </>
  );
}, isEqual);

/* -----------------------------------------------------------------------------------------------*/

const GroupLabel: ParentComponent<{ groupId: string }> = (props) => {
  const [group] = useGroup(props.groupId);

  if (!group || group.data?.is_organization_group) return null;

  return (
    <LabelChip tooltip={group.name} href={`/groups/${group.id}`} onClick={(e) => e.stopPropagation()}>
      <span className="truncate">{renderGroupName(group)}</span>
    </LabelChip>
  );
};

/* -----------------------------------------------------------------------------------------------*/

/** @summary renders any additional channels to avoid cluttering the UI. */
const GroupLabelOverflow: ParentComponent<{ groupIds: string[] }> = (props) => {
  const [tags] = useTagRecords(props.groupIds);

  if (props.groupIds.length === 0) {
    return null;
  }

  const groupNames = tags.map((g) => `#${g.name}`).join(", ");

  return (
    <Tooltip side="bottom" content={groupNames}>
      <div className={LabelOutlineClasses}>{`+${props.groupIds.length}`}</div>
    </Tooltip>
  );
};

/* -----------------------------------------------------------------------------------------------*/

export const ThreadLabelsCountChip: ParentComponent<{
  threadId: string;
}> = memo((props) => {
  const [labelIds] = useThreadLabelIds({ threadId: props.threadId });
  const [labels] = useTagRecords(labelIds);
  const params = useParams({ strict: false });

  const unarchivedLabels = useMemo(() => {
    return params.tagId ?
        // If we're on a page associated with a specific label then we
        // don't want to include that label when rendering the labels
        // (the inclusion of that label is implied and its a annoying for every
        // thread in the list to show the same label).
        labels.filter((label) => label.id !== params.tagId && !label.deleted_at && !label.archived_at)
      : labels.filter((label) => !label.deleted_at && !label.archived_at);
  }, [params.tagId, labels]);

  if (unarchivedLabels.length === 0) {
    return null;
  }

  const tooltipMsg = unarchivedLabels.map((label) => label.name).join(", ");

  return (
    <Tooltip side="bottom" content={tooltipMsg}>
      <div className={cx(LabelOutlineClasses, "flex items-center")}>
        <MdLabelOutline className="text-black" /> <span className="ml-1">{unarchivedLabels.length}</span>
      </div>
    </Tooltip>
  );
}, isEqual);

/* -----------------------------------------------------------------------------------------------*/

export const ThreadLabelChip: ParentComponent<{
  labelId: string;
  color?: "primary" | "secondary";
  showArchived?: boolean;
  onRemoveButtonClick?: (props: {
    label: LabelTagRecord;
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>;
  }) => void;
}> = (props) => {
  const [label] = useLabel(props.labelId);

  if (!label || (!props.showArchived && label.archived_at)) return null;

  const colorClassName =
    props.color === "primary" ? "bg-transparent border-black text-black hover:bg-slateA-4" : undefined;

  const onRemoveButtonClick =
    props.onRemoveButtonClick &&
    ((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
      props.onRemoveButtonClick?.({ label: label as LabelTagRecord, event: e }));

  return (
    <LabelChip
      tooltip={label.name}
      href={`/labels/${label.id}`}
      colorClassName={colorClassName}
      onClick={(e) => e.stopPropagation()}
      removeButtonTooltip="Remove label"
      onRemoveButtonClick={onRemoveButtonClick}
    >
      <MdLabelOutline className="text-black flex-shrink-0" />{" "}
      <span className="truncate ml-1">
        {label.name}
        {props.showArchived && label.archived_at && " (Archived)"}
      </span>
    </LabelChip>
  );
};

/* -----------------------------------------------------------------------------------------------*/

export const ThreadResolvedLabel: ParentComponent<{
  threadId: string;
  /**
   * If a messageId is provided, the label will only render if `thread_tag.data.message_id` matches
   * the provided messageId.
   */
  messageId?: string;
  theme?: "light" | "dark";
  onClick?: (props: {
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>;
    threadTag: ThreadResolvedThreadTagRecord;
  }) => void;
  className?: string;
  showRemoveButton?: boolean;
}> = (props) => {
  const [threadTag] = useThreadResolvedTag(props.threadId);
  const [resolverUserProfile] = useUserProfile(threadTag?.creator_user_id);

  if (!threadTag) return null;
  if (props.messageId && threadTag.data.message_id !== props.messageId) {
    return null;
  }

  let tooltip =
    props.messageId ? `This message resolved the thread.` : `This thread has been resolved. Click to view resolution.`;

  if (resolverUserProfile) {
    tooltip += ` Marked resolved by ${resolverUserProfile.name}.`;
  }

  const onRemoveButtonClick =
    !props.showRemoveButton ? undefined : (
      (e: React.MouseEvent) => {
        e.stopPropagation();

        const isSure = confirm(`Are you sure you want to mark this thread as not resolved?`);

        if (!isSure) return;

        markThreadNotResolvedCommand.trigger();
      }
    );

  return (
    <LabelChip
      colorClassName={cx(
        props.theme === "dark" ?
          `bg-greenDark-11 border-greenDark-11 text-black`
        : `bg-green-10 border-green-10 text-white`,
        props.onClick &&
          (props.theme === "dark" ?
            "hover:bg-greenDark-12 hover:border-greenDark-12"
          : "hover:bg-green-11 hover:border-green-11"),
      )}
      className={props.className}
      tooltip={tooltip}
      onClick={(event) => props.onClick?.({ event, threadTag })}
      removeButtonTooltip="Mark as not resolved"
      onRemoveButtonClick={onRemoveButtonClick}
    >
      {props.messageId ? "Resolution" : "Resolved"}
    </LabelChip>
  );
};

/* -----------------------------------------------------------------------------------------------*/

export const RemoveLabelButton: ParentComponent<{
  tooltip: string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
}> = (props) => {
  return (
    <div className={cx("relative inline group", props.className)}>
      <Tooltip side="bottom" content={props.tooltip}>
        <button
          type="button"
          className={`
            absolute z-[1] hidden -right-2 -top-2 text-red-9 group-hover:block bg-white rounded-full
          hover:text-red-11 hover:bg-red-1`}
          onClick={props.onClick}
        >
          <MdRemoveCircle />
        </button>
      </Tooltip>

      {props.children}
    </div>
  );
};

/* -----------------------------------------------------------------------------------------------*/
